import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import Reveal from '@/components/feature/Reveal';

export default function About() {
  const { t } = useTranslation();

  const stats = [
    { value: '2020', label: t('about.established'), icon: 'ri-calendar-line' },
    { value: '3', label: t('about.branches'), icon: 'ri-store-2-line' },
    { value: '20+', label: t('about.team'), icon: 'ri-team-line' },
    { value: '59', label: t('about.menuItems'), icon: 'ri-restaurant-2-line' },
  ];

  const services = [
    { icon: 'ri-briefcase-4-line', title: t('about.service.corporate'), desc: t('about.service.corporateDesc') },
    { icon: 'ri-cake-3-line', title: t('about.service.private'), desc: t('about.service.privateDesc') },
    { icon: 'ri-heart-3-line', title: t('about.service.weddings'), desc: t('about.service.weddingsDesc') },
    { icon: 'ri-group-line', title: t('about.service.social'), desc: t('about.service.socialDesc') },
    { icon: 'ri-store-3-line', title: t('about.service.exhibitions'), desc: t('about.service.exhibitionsDesc') },
    { icon: 'ri-truck-line', title: t('about.service.delivery'), desc: t('about.service.deliveryDesc') },
  ];

  const commitments = [
    { icon: 'ri-shield-check-line', title: t('about.commit.foodSafety'), desc: t('about.commit.foodSafetyDesc') },
    { icon: 'ri-hotel-bed-line', title: t('about.commit.hygiene'), desc: t('about.commit.hygieneDesc') },
    { icon: 'ri-award-line', title: t('about.commit.quality'), desc: t('about.commit.qualityDesc') },
    { icon: 'ri-emotion-happy-line', title: t('about.commit.satisfaction'), desc: t('about.commit.satisfactionDesc') },
  ];

  return (
    <Layout showTopBar>
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20modern%20cafe%20interior%20with%20warm%20ambient%20lighting%2C%20wooden%20tables%2C%20coffee%20bar%2C%20industrial%20chic%20design%2C%20cozy%20atmosphere%2C%20editorial%20photography%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20premium%20coffee%20shop%20aesthetic&width=1600&height=700&seq=about-hero-01&orientation=landscape"
          alt="BIOLA Cafe Interior"
          className="w-full h-full object-cover object-center animate-hero-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/40 to-foreground-950/60" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center max-w-3xl">
            <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-xs font-semibold rounded-full mb-4 animate-hero-content">
              {t('about.heroBadge')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight animate-hero-content-delay">
              {t('about.heroTitle')}
            </h1>
            <p className="text-background-100 text-base md:text-lg max-w-xl mx-auto leading-relaxed animate-hero-content-delay-2">
              {t('about.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {t('about.companyTitle')}
              </h2>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {t('about.companyDesc')}
              </p>
            </div>
          </Reveal>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 items-stretch">
            {stats.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 70} className="h-full">
                <div
                  className="group bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 text-center hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col justify-center"
                >
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mx-auto mb-3 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${stat.icon} text-lg md:text-xl`} />
                  </span>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1 transition-colors duration-300 group-hover:text-primary-600">
                    {stat.value}
                  </p>
                  <p className="text-foreground-500 text-sm">{stat.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-stretch">
            {/* Vision */}
            <Reveal className="h-full">
              <div className="group bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col">
                <span className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-600 rounded-xl mb-4 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-eye-line text-xl" />
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3 transition-colors duration-300 group-hover:text-primary-600">
                  {t('about.visionTitle')}
                </h3>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed flex-1">
                  {t('about.visionDesc')}
                </p>
              </div>
            </Reveal>

            {/* Mission */}
            <Reveal delay={90} className="h-full">
              <div className="group bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col">
                <span className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl mb-4 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-focus-3-line text-xl" />
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3 transition-colors duration-300 group-hover:text-primary-600">
                  {t('about.missionTitle')}
                </h3>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed flex-1">
                  {t('about.missionDesc')}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our Commitments */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('about.commitmentsTitle')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                {t('about.commitmentsDesc')}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 items-stretch">
            {commitments.map((item, i) => (
              <Reveal key={item.title} delay={i * 70} className="h-full">
                <div
                  className="group bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg mb-3 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 transition-colors duration-300 group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('about.servicesTitle')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                {t('about.servicesDesc')}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 items-stretch">
            {services.map((service, i) => (
              <Reveal key={service.title} delay={i * 60} className="h-full">
                <div
                  className="group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-3 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${service.icon} text-lg`} />
                  </span>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 transition-colors duration-300 group-hover:text-primary-600">
                    {service.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <Reveal>
          <div className="max-w-4xl mx-auto text-center">
            <span className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-600 rounded-xl mx-auto mb-4">
              <i className="ri-leaf-line text-xl" />
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t('about.sustainTitle')}
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-6">
              {t('about.sustainDesc')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto items-stretch">
              {[
                { icon: 'ri-seedling-line', title: t('about.sustain.sourcing'), desc: t('about.sustain.sourcingDesc') },
                { icon: 'ri-recycle-line', title: t('about.sustain.eco'), desc: t('about.sustain.ecoDesc') },
                { icon: 'ri-community-line', title: t('about.sustain.community'), desc: t('about.sustain.communityDesc') },
              ].map((item, i) => (
                <Reveal key={item.title} delay={i * 80} className="h-full">
                  <div className="group bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col">
                    <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mx-auto mb-3 transition-transform duration-300 group-hover:scale-110">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-1 transition-colors duration-300 group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-xs md:text-sm">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-primary-500">
        <Reveal>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-background-100 text-sm md:text-base leading-relaxed mb-6">
              {t('about.ctaDesc')}
            </p>
            <a
              href="mailto:biola.ae1@gmail.com"
              className="btn-press group inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-primary-600 font-semibold rounded-xl hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-mail-line transition-transform duration-300 group-hover:-translate-y-0.5" />
              {t('about.ctaButton')}
            </a>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}